import { Link } from 'react-router-dom';

interface ArticleCardProps {
  id: number;
  title: string;
  excerpt: string;
  coverImage: string;
  categoryName: string;
  category: string;
  author: string;
  publishedAt: string;
  views: number;
  tags: string[];
  isHot?: boolean;
  variant?: 'default' | 'featured' | 'compact';
}

export default function ArticleCard({
  id,
  title,
  excerpt,
  coverImage,
  categoryName,
  category,
  author,
  publishedAt,
  views,
  tags,
  isHot,
  variant = 'default',
}: ArticleCardProps) {
  const formatViews = (v: number) => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return v.toString();
  };

  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${id}`}
        className="article-card group block relative overflow-hidden rounded-xl"
      >
        <div className="relative h-[420px] md:h-[500px] w-full">
          <img
            src={coverImage}
            alt={title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/30 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
            <div className="flex items-center gap-2 mb-3">
              <span className="px-3 py-1 text-xs font-medium rounded-full bg-accent-500 text-background-50">
                {categoryName}
              </span>
              {isHot && (
                <span className="px-3 py-1 text-xs font-medium rounded-full bg-primary-500 text-background-50 flex items-center gap-1">
                  <i className="ri-fire-line text-xs"></i> 热门
                </span>
              )}
            </div>
            <h2 className="text-xl md:text-3xl font-heading font-bold text-background-50 group-hover:text-accent-300 transition-colors mb-2 line-clamp-2">
              {title}
            </h2>
            <p className="text-sm text-background-50/70 line-clamp-2 mb-3">{excerpt}</p>
            <div className="flex items-center gap-4 text-xs text-background-50/60">
              <span className="flex items-center gap-1"><i className="ri-user-line"></i> {author}</span>
              <span className="flex items-center gap-1"><i className="ri-calendar-line"></i> {publishedAt}</span>
              <span className="flex items-center gap-1"><i className="ri-eye-line"></i> {formatViews(views)}</span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${id}`}
        className="article-card group flex gap-3 p-3 rounded-lg hover:bg-background-100 transition-colors"
      >
        <div className="w-24 h-16 flex-shrink-0 rounded-lg overflow-hidden">
          <img src={coverImage} alt={title} className="w-full h-full object-cover object-top" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
            {title}
          </h4>
          <div className="flex items-center gap-3 mt-1.5 text-xs text-foreground-500">
            <span>{publishedAt}</span>
            <span className="flex items-center gap-1"><i className="ri-eye-line text-xs"></i> {formatViews(views)}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${id}`}
      className="article-card group block bg-background-50 rounded-xl overflow-hidden border border-background-200/70"
    >
      <div className="relative h-48 md:h-52 overflow-hidden">
        <img
          src={coverImage}
          alt={title}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute top-3 left-3 flex items-center gap-2">
          <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-accent-500 text-background-50">
            {categoryName}
          </span>
          {isHot && (
            <span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-500 text-background-50">
              <i className="ri-fire-line text-xs"></i>
            </span>
          )}
        </div>
      </div>
      <div className="p-4 md:p-5">
        <h3 className="text-base md:text-lg font-heading font-bold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
          {title}
        </h3>
        <p className="text-sm text-foreground-600 line-clamp-2 mb-3 leading-relaxed">{excerpt}</p>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 text-xs text-foreground-500">
            <span className="flex items-center gap-1"><i className="ri-user-line"></i> {author}</span>
            <span>{publishedAt}</span>
          </div>
          <span className="flex items-center gap-1 text-xs text-foreground-500">
            <i className="ri-eye-line"></i> {formatViews(views)}
          </span>
        </div>
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-background-200/50">
            {tags.slice(0, 3).map((tag) => (
              <span key={tag} className="tag-pill px-2 py-0.5 text-xs rounded-full bg-secondary-50 text-secondary-700">
                #{tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </Link>
  );
}